import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Persistent 40px banner shown in demo mode.
 * z-index 91: above sub-header (90), below app-bar (100).
 *
 * Also listens for 'demo:toast' custom events dispatched by useDemoMutation
 * and renders a transient success message for 2.5s.
 */
export function DemoBanner() {
  const { t } = useTranslation('core.demo');
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  useEffect(() => {
    function handleDemoToast(e: Event) {
      const detail = (e as CustomEvent<{ message: string }>).detail;
      setToastMessage(detail.message);
      const timer = setTimeout(() => setToastMessage(null), 2500);
      return () => clearTimeout(timer);
    }

    window.addEventListener('demo:toast', handleDemoToast);
    return () => window.removeEventListener('demo:toast', handleDemoToast);
  }, []);

  return (
    <>
      <output
        aria-atomic="true"
        aria-live="polite"
        data-testid="demo-banner"
        style={{ zIndex: 91 }}
        className="flex h-10 shrink-0 items-center justify-between gap-3 bg-[var(--color-accent)] px-4 text-[13px] text-white"
      >
        <span className="font-medium">{t('bannerText')}</span>
        <a
          href="https://app.sazeo.io/signup"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 rounded border border-white/40 px-3 py-0.5 text-[12px] font-semibold hover:bg-white/10 transition-colors"
        >
          {t('bannerCta')} →
        </a>
      </output>

      {toastMessage ? (
        <output
          aria-atomic="true"
          aria-live="assertive"
          style={{ zIndex: 200 }}
          className="fixed bottom-4 right-4 flex items-center gap-2 rounded-[6px] border border-[var(--color-border)] bg-[var(--color-surface)] px-4 py-3 text-[13px] text-[var(--color-text-primary)] shadow-[var(--shadow-md)]"
        >
          <span
            className="inline-block h-2 w-2 rounded-full bg-[var(--color-accent)] shrink-0"
            aria-hidden
          />
          {toastMessage}
        </output>
      ) : null}
    </>
  );
}
